<template>
    <div
        class="v-select"
        :class="{ 'vs--open': isOpen, 'vs--disabled': disabled }"
        ref="trigger"
    >
        <div
            class="vs__dropdown-toggle"
            @click="toggleDropdown"
        >
            <div class="vs__selected-options">
                <div
                    v-if="multiple"
                    class="vs__selected-options-container"
                >
                    <span
                        v-for="(option, index) in selectedOptions"
                        :key="index"
                        class="vs__selected"
                    >
                        {{ getOptionLabel(option) }}
                        <button
                            v-if="clearable"
                            @click.stop="removeOption(option)"
                            class="vs__deselect"
                        >
                            ×
                        </button>
                    </span>
                    <span
                        v-if="!hasValue"
                        class="vs__placeholder"
                    >
                        {{ computedPlaceholder }}
                    </span>
                </div>
                <div
                    v-else
                    class="vs__selected"
                    :class="{ vs__placeholder: !hasValue }"
                >
                    {{ selectedLabel || computedPlaceholder }}
                </div>
            </div>

            <div class="vs__actions">
                <button
                    v-if="clearable && hasValue"
                    @click.stop="clearSelection"
                    class="vs__clear"
                >
                    ×
                </button>
                <div
                    v-if="isCreatingTag"
                    class="vs__loading"
                >⏳</div>
                <div
                    v-else
                    class="vs__open-indicator"
                >▼</div>
            </div>
        </div>

        <!-- Teleported dropdown -->
        <teleport to="body">
            <div
                v-if="isOpen"
                class="vs__dropdown-menu"
                :style="dropdownStyle"
                ref="dropdown"
            >
                <div class="vs__search-box">
                    <input
                        v-if="searchable"
                        ref="searchInput"
                        v-model="searchQuery"
                        class="vs__search"
                        :placeholder="placeholder"
                        @keydown.enter.prevent="selectHighlighted"
                        @keydown.arrow-down.prevent="highlightNext"
                        @keydown.arrow-up.prevent="highlightPrevious"
                        @keydown.escape="handleEscape"
                    />
                </div>

                <div class="vs__dropdown-options">
                    <div
                        v-for="(option, index) in filteredOptions"
                        :key="getOptionValue(option)"
                        class="vs__dropdown-option"
                        :class="{
                            'vs__dropdown-option--highlight':
                                index === highlightedIndex,
                            'vs__dropdown-option--selected': isSelected(option),
                        }"
                        :data-create="option.__isCreateOption"
                        @click="selectOption(option)"
                        @mouseenter="highlightedIndex = index"
                    >
                        <slot
                            name="option"
                            :option="option"
                        >
                            {{ getOptionLabel(option) }}
                        </slot>
                    </div>

                    <div
                        v-if="filteredOptions.length === 0"
                        class="vs__no-options"
                    >
                        {{ noOptionsText }}
                    </div>
                </div>
            </div>
        </teleport>
    </div>
</template>

<script>
import { createPopper } from '@popperjs/core';
import i18n from "@/lang";
const { t } = i18n.global;

export default {
    name: "VSelect",
    props: {
        modelValue: {
            type: [String, Number, Array, Object, Boolean],
            default: null,
        },
        options: {
            type: Array,
            default: () => [],
        },
        multiple: {
            type: Boolean,
            default: false,
        },
        clearable: {
            type: Boolean,
            default: false,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        searchable: {
            type: Boolean,
            default: true,
        },
        placeholder: {
            type: String,
            default: "Select...",
        },
        noOptionsText: {
            type: String,
            default: "No options available",
        },
        label: {
            type: String,
            default: "label",
        },
        value: {
            type: String,
            default: "value",
        },
        reduce: {
            type: Function,
            default: (option) => option,
        },
        allowCreate: {
            type: Boolean,
            default: false,
        },
        remote: {
            type: Boolean,
            default: false,
        },
        remoteMethod: {
            type: Function,
            default: null,
        },
        onCreateTag: {
            type: Function,
            default: null,
        },
        placement: { type: String, default: "bottom-start" },
        offset: { type: Number, default: 8 },
        fallbackPlacements: {
            type: Array,
            default: () => ["bottom-start"],
        },
    },
    emits: [
        "update:modelValue",
        "option:selected",
        "option:deselected",
        "tag:created",
        "open",
        "close",
    ],
    data() {
        return {
            isOpen: false,
            searchQuery: "",
            highlightedIndex: -1,
            isCreatingTag: false,
            dropdownStyle: {},
            popperInstance: null,
            scrollHandler: null,
            resizeHandler: null,
        };
    },
    computed: {
        selectedOptions() {
            if (this.multiple) {
                if (!Array.isArray(this.modelValue)) return [];
                return this.modelValue
                    .map((val) => this.findOptionByValue(val))
                    .filter(Boolean);
            }
            return this.modelValue
                ? [this.findOptionByValue(this.modelValue)]
                : [];
        },
        selectedLabel() {
            if (this.multiple) return "";
            const option = this.findOptionByValue(this.modelValue);
            return option ? this.getOptionLabel(option) : "";
        },
        hasValue() {
            if (this.multiple) {
                return (
                    Array.isArray(this.modelValue) && this.modelValue.length > 0
                );
            }

            return (
                this.modelValue !== null &&
                this.modelValue !== undefined &&
                this.modelValue !== ""
            );
        },
        computedPlaceholder() {
            return this.placeholder
                ? this.placeholder
                : `${t("messages.select")} ${this.label || t("option")}`;
        },
        filteredOptions() {
            let options = this.options || [];

            if (this.searchQuery) {
                options = options.filter((option) => {
                    const label = this.getOptionLabel(option);
                    return (
                        label &&
                        label
                            .toLowerCase()
                            .includes(this.searchQuery.toLowerCase())
                    );
                });
            }

            // Add create option if allowCreate is enabled and search query doesn't match existing options
            if (
                this.allowCreate &&
                this.searchQuery &&
                this.searchQuery.trim()
            ) {
                const searchLower = this.searchQuery.toLowerCase();
                const hasMatch = options.some((option) => {
                    const label = this.getOptionLabel(option);
                    return label && label.toLowerCase() === searchLower;
                });

                if (!hasMatch) {
                    options = [
                        {
                            [this.label]: `Create "${this.searchQuery}"`,
                            [this.value]: this.searchQuery,
                            __isCreateOption: true,
                        },
                        ...options,
                    ];
                }
            }

            return options;
        },
    },
    methods: {
        toggleDropdown() {
            if (this.disabled) return;
            this.isOpen = !this.isOpen;
            if (this.isOpen) {
                this.$nextTick(() => {
                    // Add small delay for modal transforms to complete
                    setTimeout(() => {
                        this.setupPopper();
                        if (this.$refs.searchInput) {
                            try { this.$refs.searchInput.focus({ preventScroll: true }); }
                            catch (e) { this.$refs.searchInput.focus(); }
                        }
                        this.$emit('open');
                    }, 50);
                });
            } else {
                this.destroyPopper();
            }
        },
        closeDropdown() {
            this.isOpen = false;
            this.highlightedIndex = -1;
            this.destroyPopper();
        },
        setupPopper() {
            if (!this.$refs.trigger || !this.$refs.dropdown) return;

            // ✅ Destroy old instance if exists
            if (this.popperInstance) this.popperInstance.destroy();

            const dropdown = this.$refs.dropdown;
            const trigger = this.$refs.trigger;

            // ✅ Match width (same as Element Plus)
            dropdown.style.minWidth = `${trigger.offsetWidth}px`;
            dropdown.style.maxWidth = `${trigger.offsetWidth}px`;

            // ✅ Initialize Popper like Element Plus
            this.popperInstance = createPopper(this.$refs.trigger, this.$refs.dropdown, {
                placement: this.placement,
                modifiers: [
                    {
                        name: "offset",
                        options: {
                            offset: [0, this.offset],
                        },
                    },
                    {
                        name: "flip",
                        options: {
                            fallbackPlacements: this.fallbackPlacements,
                        },
                    },
                    {
                        name: "preventOverflow",
                        options: {
                            padding: 8,
                            boundary: "clippingParents",
                            altAxis: true, // ✅ allow overflow on alternate axis
                            tether: false, // ✅ don't constrain to trigger element
                        },
                    },
                    {
                        name: "computeStyles",
                        options: {
                            gpuAcceleration: false, // ✅ prevents transform issues in modals
                            adaptive: false, // ✅ use absolute positioning strategy (better for modals)
                        },
                    },
                    {
                        /** ✅ Keep width synced dynamically (like Element Plus) **/
                        name: "sameWidth",
                        enabled: true,
                        phase: "beforeWrite",
                        requires: ["computeStyles"],
                        fn: ({ state }) => {
                            const width = state.rects.reference.width;
                            state.styles.popper.minWidth = `${width}px`;
                            state.styles.popper.maxWidth = `${width}px`;
                            state.styles.popper.width = `${width}px`;
                        },
                        effect: ({ state }) => {
                            const observer = new ResizeObserver(() => {
                                if (state.elements.reference && state.elements.popper) {
                                    const width = state.elements.reference.offsetWidth;
                                    state.elements.popper.style.minWidth = `${width}px`;
                                    state.elements.popper.style.maxWidth = `${width}px`;
                                    state.elements.popper.style.width = `${width}px`;
                                }
                            });
                            observer.observe(state.elements.reference);
                            return () => observer.disconnect();
                        },
                    },
                ],
            });
        },
        destroyPopper() {
            if (this.popperInstance) {
                this.popperInstance.destroy();
                this.popperInstance = null;
            }
        },
        handleClickOutside(e) {
            if (
                !this.$refs.trigger?.contains(e.target) &&
                !this.$refs.dropdown?.contains(e.target)
            ) {
                this.isOpen = false;
                this.destroyPopper();
            }
        },
        updateDropdownPosition() {
            const wrapper = this.$refs.selectWrapper;
            const dropdown = this.$refs.dropdown;
            if (!wrapper || !dropdown) return;

            const rect = wrapper.getBoundingClientRect();
            const dropdownHeight = dropdown.offsetHeight;
            const spaceBelow = window.innerHeight - rect.bottom;
            const spaceAbove = rect.top;

            const openUp = spaceBelow < dropdownHeight && spaceAbove > dropdownHeight;

            const top = openUp
                ? rect.top - dropdownHeight - 4
                : rect.bottom + 4;

            this.dropdownStyle = {
                position: "fixed",
                inset: `${top}px auto auto ${rect.left}px`,
                width: `${rect.width}px`,
                zIndex: 9999,
            };
        },
        async selectOption(option) {
            // Handle tag creation
            if (option.__isCreateOption) {
                const newTag = this.searchQuery.trim();

                try {
                    this.isCreatingTag = true;
                    // Call the onCreateTag function to save to database
                    let savedTag = newTag;
                    if (this.onCreateTag) {
                        savedTag = await this.onCreateTag(newTag);
                    }

                    // If onCreateTag is provided, let the parent handle the selection
                    if (this.onCreateTag) {
                        console.log(
                            "Tag created successfully, parent will handle selection"
                        );
                        this.$emit("tag:created", savedTag);
                        // Prevent clearing options after tag creation for multiple select
                        this.preventClear = true;
                    } else {
                        // Fallback: handle selection in component if no onCreateTag provided
                        if (this.multiple) {
                            const currentValue = Array.isArray(this.modelValue)
                                ? [...this.modelValue]
                                : [];
                            console.log(
                                "Current value before adding:",
                                currentValue
                            );
                            console.log("Saved tag to add:", savedTag);
                            if (!currentValue.includes(savedTag)) {
                                const newValue = [...currentValue, savedTag];
                                console.log("New value to emit:", newValue);
                                this.$emit("update:modelValue", newValue);
                                this.$emit("option:selected", {
                                    [this.label]: savedTag,
                                    [this.value]: savedTag,
                                });
                                this.$emit("tag:created", savedTag);
                                this.preventClear = true;
                            } else {
                                console.log("Tag already exists in selection");
                            }
                        } else {
                            console.log(
                                "Single select - setting value to:",
                                savedTag
                            );
                            this.$emit("update:modelValue", savedTag);
                            this.$emit("option:selected", {
                                [this.label]: savedTag,
                                [this.value]: savedTag,
                            });
                            this.$emit("tag:created", savedTag);
                            this.closeDropdown();
                        }
                    }
                } catch (error) {
                    console.error("Error creating tag:", error);
                    // Optionally show error message to user
                } finally {
                    this.isCreatingTag = false;
                }

                // Don't clear search query to maintain options visibility
                return;
            }

            if (this.multiple) {
                console.log("ssss");

                const currentValue = Array.isArray(this.modelValue)
                    ? [...this.modelValue]
                    : [];
                const optionValue = this.getOptionValue(option);

                if (currentValue.includes(optionValue)) {
                    const newValue = currentValue.filter(
                        (val) => val !== optionValue
                    );
                    this.$emit("update:modelValue", newValue);
                    this.$emit("option:deselected", option);
                } else {
                    const newValue = [...currentValue, optionValue];
                    this.$emit("update:modelValue", newValue);
                    this.$emit("option:selected", option);
                    // Prevent clearing options after selection for multiple select
                    this.preventClear = true;
                }
            } else {
                const optionValue = this.getOptionValue(option);
                this.$emit("update:modelValue", optionValue);
                this.$emit("option:selected", option);
                // this.closeDropdown();
            }
            this.closeDropdown();
        },
        removeOption(option) {
            if (this.multiple) {
                const currentValue = Array.isArray(this.modelValue)
                    ? [...this.modelValue]
                    : [];
                const optionValue = this.getOptionValue(option);
                const newValue = currentValue.filter(
                    (val) => val !== optionValue
                );
                this.$emit("update:modelValue", newValue);
                this.$emit("option:deselected", option);
            }
        },
        clearSelection() {
            this.$emit("update:modelValue", this.multiple ? [] : null);
        },
        getOptionLabel(option) {
            if (!option) return "";
            if (typeof option === "string") return option;
            if (typeof option === "number") return String(option);
            return (
                option[this.label] ||
                option.label ||
                option.name ||
                String(option)
            );
        },
        getOptionValue(option) {
            if (typeof option === "string") return option;
            return this.reduce(option);
        },
        findOptionByValue(value) {
            if (!this.options || !Array.isArray(this.options)) return null;
            return this.options.find(
                (option) => this.getOptionValue(option) === value
            );
        },
        isSelected(option) {
            const optionValue = this.getOptionValue(option);
            if (this.multiple) {
                return (
                    Array.isArray(this.modelValue) &&
                    this.modelValue.includes(optionValue)
                );
            }
            return this.modelValue === optionValue;
        },
        selectHighlighted() {
            if (
                this.highlightedIndex >= 0 &&
                this.filteredOptions[this.highlightedIndex]
            ) {
                this.selectOption(this.filteredOptions[this.highlightedIndex]);
            }
        },
        highlightNext() {
            if (this.highlightedIndex < this.filteredOptions.length - 1) {
                this.highlightedIndex++;
            }
        },
        highlightPrevious() {
            if (this.highlightedIndex > 0) {
                this.highlightedIndex--;
            }
        },
        handleRemoteSearch(query) {
            if (this.remote && this.remoteMethod) {
                console.log(
                    "Remote search called with query:",
                    query,
                    "preventClear:",
                    this.preventClear
                );
                // Always call remoteMethod - let the parent handle what to show
                this.remoteMethod(query);
            }
        },
        handleEscape() {
            this.closeDropdown();
            this.searchQuery = "";
        },
        resetPreventClear() {
            this.preventClear = false;
        },
    },
    watch: {
        searchQuery: {
            handler(newQuery, oldQuery) {
                console.log(
                    "Search query changed from:",
                    oldQuery,
                    "to:",
                    newQuery
                );

                if (this.remote && this.remoteMethod) {
                    // For local search, call immediately without debouncing
                    this.handleRemoteSearch(newQuery);
                }
            },
            immediate: false,
        },
    },
    mounted() {
        document.addEventListener("click", (e) => {
            if (!this.$el.contains(e.target)) {
                this.closeDropdown();
                // Clear search query when clicking outside
                this.searchQuery = "";
            }
        });
        window.addEventListener("click", this.handleClickOutside);
        
        // ✅ Update Popper position on scroll (for modals and containers)
        this.scrollHandler = () => {
            if (this.popperInstance && this.isOpen) {
                this.popperInstance.update();
            }
        };
        window.addEventListener("scroll", this.scrollHandler, true);
        
        // ✅ Update Popper position on resize
        this.resizeHandler = () => {
            if (this.popperInstance && this.isOpen) {
                this.popperInstance.update();
            }
        };
        window.addEventListener("resize", this.resizeHandler);
    },
    beforeUnmount() {
        // Clean up timeout when component is destroyed
        if (this.searchTimeout) {
            clearTimeout(this.searchTimeout);
        }
        this.destroyPopper();
        window.removeEventListener("click", this.handleClickOutside);
        
        // ✅ Remove scroll and resize handlers
        if (this.scrollHandler) {
            window.removeEventListener("scroll", this.scrollHandler, true);
        }
        if (this.resizeHandler) {
            window.removeEventListener("resize", this.resizeHandler);
        }
    },
};
</script>

<style scoped>
.v-select {
    position: relative;
    display: block;
}

.vs__dropdown-toggle {
    display: flex;
    align-items: center;
    min-height: 48px;
    padding: 8px 12px;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    background: white;
    cursor: pointer;
    transition: border-color 0.2s, box-shadow 0.2s;
}

.vs--focused .vs__dropdown-toggle {
    border-color: #9b8cff;
    box-shadow: inset 0 0 0 2px #9b8cff, 0 0 0 3px rgba(155, 140, 255, 0.25);
}

.vs--disabled .vs__dropdown-toggle {
    background-color: #f9fafb;
    color: #6b7280;
    cursor: not-allowed;
}

.vs__selected-options {
    flex: 1;
    display: flex;
    align-items: center;
    min-height: 32px;
}

.vs__selected-options-container {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
}

.vs__selected {
    background-color: #f8f8f8;
    color: #000;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    padding: 4px 8px;
    font-size: 14px;
    font-weight: 500;
    display: inline-flex;
    align-items: center;
    gap: 4px;
}

.vs__placeholder {
    background-color: transparent;
    color: #9ca3af;
    font-weight: normal;
    border: none;
    padding: 0;
}

.vs__deselect {
    background: none;
    border: none;
    color: #6b7280;
    cursor: pointer;
    font-size: 16px;
    line-height: 1;
    padding: 0;
    margin-left: 4px;
}

.vs__actions {
    display: flex;
    align-items: center;
    gap: 8px;
}

.vs__clear {
    background: none;
    border: none;
    color: #6b7280;
    cursor: pointer;
    font-size: 16px;
    line-height: 1;
    padding: 4px;
}

.vs__open-indicator {
    color: #6b7280;
    font-size: 12px;
    transition: transform 0.2s;
}

.vs__loading {
    color: #9b8cff;
    font-size: 12px;
    animation: spin 1s linear infinite;
}

@keyframes spin {
    from {
        transform: rotate(0deg);
    }

    to {
        transform: rotate(360deg);
    }
}

.vs--open .vs__open-indicator {
    transform: rotate(180deg);
}

.vs__dropdown-menu {
    /* position: absolute;
    top: 100%;
    left: 0;
    right: 0; */
    z-index: 9999;
    background: white;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    box-shadow: 0 8px 24px rgba(16, 24, 40, 0.08);
    margin-top: 4px;
    max-height: 300px;
    overflow: hidden;
    box-sizing: border-box;
}

.vs__search-box {
    padding: 8px;
    border-bottom: 1px solid #e2e8f0;
}

.vs__search {
    width: 100%;
    border: none;
    outline: none;
    padding: 8px;
    font-size: 14px;
    color: #111827;
}

.vs__search::placeholder {
    color: #9ca3af;
}

.vs__dropdown-options {
    max-height: 200px;
    overflow-y: auto;
}

.vs__dropdown-option {
    padding: 8px 12px;
    font-size: 14px;
    cursor: pointer;
    transition: background-color 0.2s;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.vs__dropdown-option:hover,
.vs__dropdown-option--highlight {
    background-color: #f3f4f6;
    color: #111827;
}

.vs__dropdown-option--selected {
    background-color: #9b8cff;
    color: white;
}

.vs__dropdown-option[data-create] {
    background-color: #f0f9ff;
    color: #0369a1;
    font-style: italic;
}

.vs__dropdown-option[data-create]:hover {
    background-color: #e0f2fe;
}

.vs__no-options {
    padding: 8px 12px;
    font-size: 14px;
    color: #6b7280;
    text-align: center;
}
</style>
